import { useState, useEffect, useRef, useCallback } from 'react';
import { Capacitor } from '@capacitor/core';
import { SpeechRecognition } from '@capacitor-community/speech-recognition';

// One-shot dictation for the Ask Bloom assistants, over two backends:
//
//   web    — the Web Speech API (Chrome/Safari desktop + mobile browsers)
//   native — @capacitor-community/speech-recognition
//
// The native path exists because the Android WebView that Capacitor runs the
// app in does NOT implement the Web Speech API (Chrome for Android does, the
// WebView doesn't). Detecting only `window.webkitSpeechRecognition` therefore
// left the mic button permanently disabled in the packaged app.
//
// Both backends are one-shot: listen until the speaker stops, then hand back a
// single final transcript. Callers get {supported, listening, toggle, stop}.

const WebSpeechRecognition =
    typeof window !== 'undefined' && (window.SpeechRecognition || window.webkitSpeechRecognition);

const isNative = Capacitor.isNativePlatform();
const isIOS = isNative && Capacitor.getPlatform() === 'ios';

// iOS has no endpointing. Android's SpeechRecognizer stops on its own once the
// speaker goes quiet, and the Web Speech API does the same, but iOS's
// SFSpeechRecognizer holds the mic until it is explicitly stopped — it only
// reports `isFinal` after `endAudio()`. Left alone the button sticks in
// "listening" forever, so on iOS we stream partial results and close the
// session ourselves once they stop arriving.
// Apple's Speech framework has no endpointing — unlike Android's
// SpeechRecognizer, it never stops on its own, so the silence call is ours to
// make. We time it from *changes* to the transcript rather than from raw
// partial-result events: the recognizer keeps emitting events with unchanged
// text after you stop talking, and re-arming on those was adding seconds of
// dead air before the question sent.
const SILENCE_MS = 2500;       // quiet time that ends an utterance, once speech has started
const NO_SPEECH_MS = 10000;    // longer grace before the first word — thinking time isn't silence
const MAX_SESSION_MS = 60000;  // backstop so a hot mic can never run away

// Neither backend can auto-detect the spoken language, so we dictate in the
// device's language; the backend then detects/translates the transcript to
// English (same path as typed text) before sending it to Claude.
const DICTATION_LOCALE =
    (typeof navigator !== 'undefined' && (navigator.language || (navigator.languages && navigator.languages[0]))) || 'en-US';

export function useDictation(onResult) {
    // On native, availability is an async device query (a device with no
    // recognition service installed reports false), so it starts unknown.
    const [supported, setSupported] = useState(isNative ? false : !!WebSpeechRecognition);
    const [listening, setListening] = useState(false);

    const recognitionRef = useRef(null);   // web: the live SpeechRecognition instance
    const listeningRef = useRef(false);    // read by stop() without re-creating the callback

    // iOS-only: the latest partial transcript, the silence/backstop timers, and
    // the partialResults subscription, all torn down together in finishIOS.
    const partialRef = useRef('');
    const silenceTimerRef = useRef(null);
    const maxTimerRef = useRef(null);
    const listenerRef = useRef(null);

    // Hold the latest callback in a ref so `toggle` stays referentially stable
    // even though callers pass a fresh closure each render.
    const onResultRef = useRef(onResult);
    useEffect(() => { onResultRef.current = onResult; }, [onResult]);

    const setListeningBoth = useCallback((v) => {
        listeningRef.current = v;
        setListening(v);
    }, []);

    useEffect(() => {
        if (!isNative) return undefined;
        let cancelled = false;
        SpeechRecognition.available()
            .then(({ available }) => { if (!cancelled) setSupported(!!available); })
            .catch(() => { if (!cancelled) setSupported(false); });
        return () => { cancelled = true; };
    }, []);

    const clearTimers = useCallback(() => {
        clearTimeout(silenceTimerRef.current);
        clearTimeout(maxTimerRef.current);
        silenceTimerRef.current = null;
        maxTimerRef.current = null;
    }, []);

    // Ends an iOS session and delivers whatever was heard. Also the manual-tap
    // path, so tapping the button a second time still submits the utterance
    // rather than discarding it.
    const finishIOS = useCallback(async () => {
        clearTimers();
        try { await SpeechRecognition.stop(); } catch { /* already stopped */ }
        try { await listenerRef.current?.remove(); } catch { /* never attached */ }
        listenerRef.current = null;
        setListeningBoth(false);
        const transcript = partialRef.current.trim();
        partialRef.current = '';
        if (transcript) onResultRef.current?.(transcript);
    }, [clearTimers, setListeningBoth]);

    const stop = useCallback(() => {
        if (!listeningRef.current) return;
        if (isIOS) {
            finishIOS();
        } else if (isNative) {
            SpeechRecognition.stop().catch(() => {});
            setListeningBoth(false);
        } else {
            recognitionRef.current?.stop();   // onend flips `listening` off
        }
    }, [setListeningBoth, finishIOS]);

    // Stop a live session if the component unmounts mid-utterance, otherwise
    // the recognizer keeps holding the mic.
    useEffect(() => () => {
        clearTimeout(silenceTimerRef.current);
        clearTimeout(maxTimerRef.current);
        listenerRef.current?.remove().catch(() => {});
        if (!listeningRef.current) return;
        if (isNative) SpeechRecognition.stop().catch(() => {});
        else recognitionRef.current?.stop();
    }, []);

    const startNative = useCallback(async () => {
        let state;
        try {
            ({ speechRecognition: state } = await SpeechRecognition.checkPermissions());
            if (state !== 'granted') {
                ({ speechRecognition: state } = await SpeechRecognition.requestPermissions());
            }
        } catch {
            setListeningBoth(false);
            return;
        }
        // Denied at the OS prompt — leave the button idle rather than opening a
        // recognizer that would immediately error.
        if (state !== 'granted') { setListeningBoth(false); return; }

        setListeningBoth(true);

        // iOS: stream partials and end the session ourselves after SILENCE_MS of
        // no new text. start() resolves immediately when partialResults is true
        // and carries no transcript, so the partials are the only source.
        if (isIOS) {
            partialRef.current = '';
            let heardSpeech = false;
            const armSilence = () => {
                clearTimeout(silenceTimerRef.current);
                silenceTimerRef.current = setTimeout(
                    () => { finishIOS(); },
                    heardSpeech ? SILENCE_MS : NO_SPEECH_MS,
                );
            };
            try {
                listenerRef.current = await SpeechRecognition.addListener('partialResults', ({ matches }) => {
                    const t = matches?.[0];
                    // Only new words count as "still speaking". Events that repeat
                    // the existing transcript mean the recognizer is idling, not
                    // that the speaker is mid-sentence, so they must not reset the
                    // clock or the send lags well behind the last word.
                    if (t && t !== partialRef.current) {
                        partialRef.current = t;
                        heardSpeech = true;
                        armSilence();
                    }
                });
                armSilence();   // also covers "opened the mic and said nothing"
                maxTimerRef.current = setTimeout(() => { finishIOS(); }, MAX_SESSION_MS);
                await SpeechRecognition.start({
                    language: DICTATION_LOCALE,
                    maxResults: 1,
                    partialResults: true,
                    popup: false,
                });
            } catch {
                finishIOS();
            }
            return;
        }

        try {
            // popup:false keeps dictation inline (the system dialog would cover
            // the thread and swallow partialResults); maxResults:1 mirrors the
            // web path's maxAlternatives=1.
            const { matches } = await SpeechRecognition.start({
                language: DICTATION_LOCALE,
                maxResults: 1,
                partialResults: false,
                popup: false,
            });
            const transcript = matches?.[0];
            if (transcript) onResultRef.current?.(transcript);
        } catch {
            // no-speech / cancelled / recognizer busy — fall through to idle
        } finally {
            setListeningBoth(false);
        }
    }, [setListeningBoth, finishIOS]);

    const startWeb = useCallback(() => {
        const rec = new WebSpeechRecognition();
        rec.lang = DICTATION_LOCALE;
        rec.interimResults = false;
        rec.maxAlternatives = 1;
        rec.onresult = (e) => {
            const transcript = e.results[0][0].transcript;
            if (transcript) onResultRef.current?.(transcript);
        };
        rec.onend = () => setListeningBoth(false);
        rec.onerror = () => setListeningBoth(false);
        recognitionRef.current = rec;
        setListeningBoth(true);
        rec.start();
    }, [setListeningBoth]);

    const toggle = useCallback(() => {
        if (!supported) return;
        if (listeningRef.current) { stop(); return; }
        if (isNative) startNative();
        else startWeb();
    }, [supported, stop, startNative, startWeb]);

    return { supported, listening, toggle, stop };
}
